<template>
  <div>
    <StepHeader
      class="address-select__title"
      title="Where Are You Looking for a Vehicle?"
    />

    <StepContent
      :width="stepWidth"
    >
      <AppCanadaPost
        :error="error"
        :unverified-address="!verified && !addressFallback"
        @input="submitted = false"
        @fallback-validated="handleFallbackValidated()"
      />
    </StepContent>

    <StepFooter>
      <AppButtonContinue
        @click="handleContinue()"
      >
        Continue
      </AppButtonContinue>

      <StepTooltip>
        Providing your location helps find the best deals near you.
      </StepTooltip>
    </StepFooter>

    <AppPopupSearching
      v-show="showSearchingPopup"
      :show="showSearchingPopup"
      @hide="handlePopupHide()"
    />
  </div>
</template>

<script>
import AppButtonContinue from '@/components/common/buttons/AppButtonContinue'
import AppCanadaPost from '../canada-post/AppCanadaPost'
import AppPopupSearching from '@/components/common/popups/AppPopupSearching'
import datalayer from '@/data-layer'
import scroll from '@/mixins/scroll'
import StepContent from '@/components/common/step/StepContent'
import StepFooter from '@/components/common/step/StepFooter'
import StepHeader from '@/components/common/step/StepHeader'
import StepTooltip from '@/components/common/step/StepTooltip'
import validation from '@/mixins/validation'

export default {
  // Name
  name: 'address-select',

  // Components
  components: {
    StepHeader,
    StepContent,
    StepFooter,
    AppCanadaPost,
    AppButtonContinue,
    StepTooltip,
    AppPopupSearching
  },

  // mixins
  mixins: [scroll, validation],

  // Data
  data () {
    return {
      datalayerContinue: 'address_continue',
      showSearchingPopup: false,
      submitted: false,
      addressFallbackIsValid: false
    }
  },

  // Computed
  computed: {
    error () {
      if (!this.submitted) return
      if (this.addressFallback && !this.addressFallbackIsValid) return 'Invalid Address'
      return !this.verified && !this.addressFallback
    },

    verified () {
      return this.$store.state.application.c_post_verified
    },

    addressFallback () {
      return this.$store.state.addressFallback
    },

    stepWidth () {
      return this.addressFallback
        ? 'xl'
        : 'sm'
    },

    stepIsValid () {
      return !this.error
    }
  },

  watch: {
    submitted (newValue) {
      if (newValue && this.addressFallback) {
        this.$bus.$emit('validate-fallback')
      }
    }
  },

  // Methods
  methods: {
    /**
     * Handle Continue Button Click
     * @emits next
     */
    async handleContinue () {
      this.submitted = true
      try {
        const isValid = await this.checkAddress()
        if (isValid) {
          datalayer.pushEvent('event', this.datalayerContinue)
          datalayer.pushEvent('event', 'cp_verified')
          this.showSearchingPopup = true
        } else {
          this.$nextTick(() => {
            this.$_scroll_toElement('.has-error', 1000)
          })
        }
      } catch (error) {
        console.warn(error)
      }
    },

    /**
     * Handle Address Fallback Validated event
     * @emits next
     */
    async handleFallbackValidated () {
      this.addressFallbackIsValid = true
      datalayer.pushEvent('event', this.datalayerContinue)
      datalayer.pushEvent('event', 'cp_unverified')
      this.showSearchingPopup = true
    },

    /**
     * Handle Popup Hide
     * Hide the popup and to to the next step
     * @emits next
     */
    handlePopupHide () {
      this.showSearchingPopup = false
      this.$nextTick(() => {
        this.$emit('next')
      })
    },

    /**
     * Check to see if the address is verified
     * @returns {Boolean}
     */
    checkAddress () {
      if (this.addressFallback) {
        this.$bus.$emit('validate-fallback')
        return false
      } else if (this.error) {
        return false
      }
      return true
    }
  }
}
</script>

<style scoped lang="scss">
.address-select__title {
  max-width: 230px;
  margin: auto;
  @include breakpoint('mobile-large-up') {
    max-width: 100%;
  }
}
</style>
